// Settings management functions
// Settings are stored in the admin/settings document
import {
  doc,
  getDoc,
  setDoc,
  serverTimestamp
} from 'firebase/firestore'
import { db } from './config'

const ADMIN_SETTINGS_DOC = 'admin/settings'

/**
 * Get Claude AI API key from settings
 * @returns {Promise<string|null>} API key or null if not set
 */
export const getClaudeApiKey = async () => {
  try {
    const settingsRef = doc(db, ADMIN_SETTINGS_DOC)
    const docSnap = await getDoc(settingsRef)
    
    if (docSnap.exists()) {
      const data = docSnap.data()
      return data.claudeApiKey || null
    }
    return null
  } catch (error) {
    console.error('Error getting Claude API key:', error)
    throw error
  }
}

/**
 * Save Claude AI API key to settings
 * @param {string} apiKey - The API key to save
 * @returns {Promise<void>}
 */
export const saveClaudeApiKey = async (apiKey) => {
  try {
    const settingsRef = doc(db, ADMIN_SETTINGS_DOC)
    const docSnap = await getDoc(settingsRef)
    
    if (docSnap.exists()) {
      // Update existing document
      await setDoc(settingsRef, {
        claudeApiKey: apiKey.trim(),
        updatedAt: serverTimestamp()
      }, { merge: true })
    } else {
      // Create new document
      await setDoc(settingsRef, {
        claudeApiKey: apiKey.trim(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      })
    }
  } catch (error) {
    console.error('Error saving Claude API key:', error)
    throw error
  }
}

/**
 * Get default prompt from settings
 * @returns {Promise<string|null>} Default prompt or null if not set
 */
export const getClaudeDefaultPrompt = async () => {
  try {
    const settingsRef = doc(db, ADMIN_SETTINGS_DOC)
    const docSnap = await getDoc(settingsRef)
    
    if (docSnap.exists()) {
      const data = docSnap.data()
      return data.claudeDefaultPrompt || null
    }
    return null
  } catch (error) {
    console.error('Error getting Claude default prompt:', error)
    throw error
  }
}

/**
 * Save Claude AI settings (API key and/or default prompt)
 * @param {Object} settings - Settings object with claudeApiKey and/or claudeDefaultPrompt
 * @returns {Promise<void>}
 */
export const saveClaudeSettings = async (settings) => {
  try {
    const settingsRef = doc(db, ADMIN_SETTINGS_DOC)
    const docSnap = await getDoc(settingsRef)
    
    const updateData = {
      updatedAt: serverTimestamp()
    }
    
    if (settings.claudeApiKey !== undefined) {
      updateData.claudeApiKey = settings.claudeApiKey.trim()
    }
    
    if (settings.claudeDefaultPrompt !== undefined) {
      updateData.claudeDefaultPrompt = settings.claudeDefaultPrompt.trim()
    }
    
    if (docSnap.exists()) {
      // Update existing document
      await setDoc(settingsRef, updateData, { merge: true })
    } else {
      // Create new document
      await setDoc(settingsRef, {
        ...updateData,
        createdAt: serverTimestamp()
      })
    }
  } catch (error) {
    console.error('Error saving Claude settings:', error)
    throw error
  }
}

/**
 * Get all settings
 * @returns {Promise<Object|null>} Settings object or null
 */
export const getSettings = async () => {
  try {
    const settingsRef = doc(db, ADMIN_SETTINGS_DOC)
    const docSnap = await getDoc(settingsRef)
    
    if (docSnap.exists()) {
      return docSnap.data()
    }
    return null
  } catch (error) {
    console.error('Error getting settings:', error)
    throw error
  }
}

/**
 * Get FTP settings
 * @returns {Promise<Object|null>} FTP settings object with host, port, username, destinationFolder, and password (masked) or null
 */
export const getFtpSettings = async () => {
  try {
    const settingsRef = doc(db, ADMIN_SETTINGS_DOC)
    const docSnap = await getDoc(settingsRef)
    
    if (docSnap.exists()) {
      const data = docSnap.data()
      return {
        host: data.ftpHost || null,
        port: data.ftpPort || null,
        username: data.ftpUsername || null,
        destinationFolder: data.ftpDestinationFolder || null,
        password: data.ftpPassword ? '***' : null // Mask password for security
      }
    }
    return null
  } catch (error) {
    console.error('Error getting FTP settings:', error)
    throw error
  }
}

/**
 * Save FTP settings
 * @param {Object} settings - Settings object with ftpHost, ftpPort, ftpUsername, ftpDestinationFolder, and/or ftpPassword
 * @returns {Promise<void>}
 */
export const saveFtpSettings = async (settings) => {
  try {
    const settingsRef = doc(db, ADMIN_SETTINGS_DOC)
    const docSnap = await getDoc(settingsRef)
    
    const updateData = {
      updatedAt: serverTimestamp()
    }
    
    if (settings.ftpHost !== undefined) {
      updateData.ftpHost = settings.ftpHost.trim()
    }
    
    if (settings.ftpPort !== undefined) {
      updateData.ftpPort = settings.ftpPort.trim()
    }
    
    if (settings.ftpUsername !== undefined) {
      updateData.ftpUsername = settings.ftpUsername.trim()
    }
    
    if (settings.ftpDestinationFolder !== undefined) {
      updateData.ftpDestinationFolder = settings.ftpDestinationFolder.trim()
    }
    
    // Only update password if a new one is provided
    if (settings.ftpPassword !== undefined && settings.ftpPassword.trim()) {
      updateData.ftpPassword = settings.ftpPassword.trim()
    }
    
    if (docSnap.exists()) {
      // Update existing document
      await setDoc(settingsRef, updateData, { merge: true })
    } else {
      // Create new document
      await setDoc(settingsRef, {
        ...updateData,
        createdAt: serverTimestamp()
      })
    }
  } catch (error) {
    console.error('Error saving FTP settings:', error)
    throw error
  }
}

/**
 * Get HTML template from settings
 * @returns {Promise<string|null>} HTML template or null if not set
 */
export const getHtmlTemplate = async () => {
  try {
    const settingsRef = doc(db, ADMIN_SETTINGS_DOC)
    const docSnap = await getDoc(settingsRef)
    
    if (docSnap.exists()) {
      const data = docSnap.data()
      // Support migration from old htmlHeader/htmlFooter to htmlTemplate
      if (data.htmlTemplate) {
        return data.htmlTemplate
      } else if (data.htmlHeader || data.htmlFooter) {
        // Combine old header and footer if template doesn't exist
        const header = data.htmlHeader || ''
        const footer = data.htmlFooter || ''
        return header + footer
      }
      return null
    }
    return null
  } catch (error) {
    console.error('Error getting HTML template:', error)
    throw error
  }
}

/**
 * Save HTML template
 * @param {string} template - Complete HTML template string
 * @returns {Promise<void>}
 */
export const saveHtmlTemplate = async (template) => {
  try {
    const settingsRef = doc(db, ADMIN_SETTINGS_DOC)
    const docSnap = await getDoc(settingsRef)
    
    const updateData = {
      htmlTemplate: template,
      updatedAt: serverTimestamp()
    }
    
    if (docSnap.exists()) {
      // Update existing document
      await setDoc(settingsRef, updateData, { merge: true })
    } else {
      // Create new document
      await setDoc(settingsRef, {
        ...updateData,
        createdAt: serverTimestamp()
      })
    }
  } catch (error) {
    console.error('Error saving HTML template:', error)
    throw error
  }
}

/**
 * Get sitemap template from settings
 * @returns {Promise<string|null>} Sitemap template or null if not set
 */
export const getSitemapTemplate = async () => {
  try {
    const settingsRef = doc(db, ADMIN_SETTINGS_DOC)
    const docSnap = await getDoc(settingsRef)
    
    if (docSnap.exists()) {
      const data = docSnap.data()
      return data.sitemapTemplate || null
    }
    return null
  } catch (error) {
    console.error('Error getting sitemap template:', error)
    throw error
  }
}

/**
 * Save sitemap template
 * @param {string} template - Sitemap template string
 * @returns {Promise<void>}
 */
export const saveSitemapTemplate = async (template) => {
  try {
    const settingsRef = doc(db, ADMIN_SETTINGS_DOC)
    const docSnap = await getDoc(settingsRef)
    
    const updateData = {
      sitemapTemplate: template,
      updatedAt: serverTimestamp()
    }
    
    if (docSnap.exists()) {
      // Update existing document
      await setDoc(settingsRef, updateData, { merge: true })
    } else {
      // Create new document
      await setDoc(settingsRef, {
        ...updateData,
        createdAt: serverTimestamp()
      })
    }
  } catch (error) {
    console.error('Error saving sitemap template:', error)
    throw error
  }
}

/**
 * Get HTML index template from settings (template for listing articles)
 * @returns {Promise<string|null>} HTML index template or null if not set
 */
export const getHtmlIndexTemplate = async () => {
  try {
    const settingsRef = doc(db, ADMIN_SETTINGS_DOC)
    const docSnap = await getDoc(settingsRef)

    if (docSnap.exists()) {
      const data = docSnap.data()
      return data.htmlIndexTemplate || null
    }
    return null
  } catch (error) {
    console.error('Error getting HTML index template:', error)
    throw error
  }
}

/**
 * Save HTML index template (template for listing articles)
 * @param {string} template - HTML index template string
 * @returns {Promise<void>}
 */
export const saveHtmlIndexTemplate = async (template) => {
  try {
    const settingsRef = doc(db, ADMIN_SETTINGS_DOC)
    const docSnap = await getDoc(settingsRef)

    const updateData = {
      htmlIndexTemplate: template,
      updatedAt: serverTimestamp()
    }

    if (docSnap.exists()) {
      await setDoc(settingsRef, updateData, { merge: true })
    } else {
      await setDoc(settingsRef, {
        ...updateData,
        createdAt: serverTimestamp()
      })
    }
  } catch (error) {
    console.error('Error saving HTML index template:', error)
    throw error
  }
}

/**
 * Get DevSecOps article index templates from settings (JSON templates)
 * @returns {Promise<Object>} Object with dev, sec, ops template strings
 */
export const getDevSecOpsArticleIndexTemplates = async () => {
  try {
    const settingsRef = doc(db, ADMIN_SETTINGS_DOC)
    const docSnap = await getDoc(settingsRef)

    if (docSnap.exists()) {
      const data = docSnap.data()
      return {
        dev: data.devSecOpsArticleIndexTemplateDev || '',
        sec: data.devSecOpsArticleIndexTemplateSec || '',
        ops: data.devSecOpsArticleIndexTemplateOps || ''
      }
    }
    return { dev: '', sec: '', ops: '' }
  } catch (error) {
    console.error('Error getting DevSecOps article index templates:', error)
    throw error
  }
}

/**
 * Save DevSecOps article index templates (JSON templates)
 * @param {Object} templates - Object with dev, sec, ops template strings
 * @returns {Promise<void>}
 */
export const saveDevSecOpsArticleIndexTemplates = async (templates) => {
  try {
    const settingsRef = doc(db, ADMIN_SETTINGS_DOC)
    const docSnap = await getDoc(settingsRef)

    const updateData = {
      devSecOpsArticleIndexTemplateDev: templates.dev || '',
      devSecOpsArticleIndexTemplateSec: templates.sec || '',
      devSecOpsArticleIndexTemplateOps: templates.ops || '',
      updatedAt: serverTimestamp()
    }

    if (docSnap.exists()) {
      await setDoc(settingsRef, updateData, { merge: true })
    } else {
      await setDoc(settingsRef, {
        ...updateData,
        createdAt: serverTimestamp()
      })
    }
  } catch (error) {
    console.error('Error saving DevSecOps article index templates:', error)
    throw error
  }
}

/**
 * Get LinkedIn settings
 * @returns {Promise<Object|null>} LinkedIn settings object with clientId, primaryClientSecret (masked), secondaryClientSecret (masked), oauthAccessToken (masked), and authorizedRedirectUrls or null
 */
export const getLinkedInSettings = async () => {
  try {
    const settingsRef = doc(db, ADMIN_SETTINGS_DOC)
    const docSnap = await getDoc(settingsRef)
    
    if (docSnap.exists()) {
      const data = docSnap.data()
      return {
        clientId: data.linkedInClientId || null,
        primaryClientSecret: data.linkedInPrimaryClientSecret ? '***' : null, // Mask secret for security
        secondaryClientSecret: data.linkedInSecondaryClientSecret ? '***' : null, // Mask secret for security
        oauthAccessToken: data.linkedInOAuthAccessToken ? '***' : null, // Mask token for security
        authorizedRedirectUrls: data.linkedInAuthorizedRedirectUrls || null,
        organizationId: data.linkedInOrganizationId || null,
        companyPageUrl: data.linkedInCompanyPageUrl || null
      }
    }
    return null
  } catch (error) {
    console.error('Error getting LinkedIn settings:', error)
    throw error
  }
}

/**
 * Save LinkedIn settings
 * @param {Object} settings - Settings object with linkedInClientId, linkedInPrimaryClientSecret, linkedInSecondaryClientSecret, linkedInOAuthAccessToken, and/or linkedInAuthorizedRedirectUrls
 * @returns {Promise<void>}
 */
export const saveLinkedInSettings = async (settings) => {
  try {
    const settingsRef = doc(db, ADMIN_SETTINGS_DOC)
    const docSnap = await getDoc(settingsRef)
    
    const updateData = {
      updatedAt: serverTimestamp()
    }
    
    if (settings.linkedInClientId !== undefined) {
      updateData.linkedInClientId = settings.linkedInClientId.trim()
    }
    
    // Only update primary client secret if a new one is provided
    if (settings.linkedInPrimaryClientSecret !== undefined && settings.linkedInPrimaryClientSecret.trim()) {
      updateData.linkedInPrimaryClientSecret = settings.linkedInPrimaryClientSecret.trim()
    }
    
    // Only update secondary client secret if a new one is provided
    if (settings.linkedInSecondaryClientSecret !== undefined && settings.linkedInSecondaryClientSecret.trim()) {
      updateData.linkedInSecondaryClientSecret = settings.linkedInSecondaryClientSecret.trim()
    }
    
    // Only update OAuth access token if a new one is provided
    if (settings.linkedInOAuthAccessToken !== undefined && settings.linkedInOAuthAccessToken.trim()) {
      updateData.linkedInOAuthAccessToken = settings.linkedInOAuthAccessToken.trim()
    }
    
    if (settings.linkedInAuthorizedRedirectUrls !== undefined) {
      updateData.linkedInAuthorizedRedirectUrls = settings.linkedInAuthorizedRedirectUrls.trim()
    }
    
    if (settings.linkedInOrganizationId !== undefined) {
      updateData.linkedInOrganizationId = settings.linkedInOrganizationId.trim()
    }
    
    if (settings.linkedInCompanyPageUrl !== undefined) {
      updateData.linkedInCompanyPageUrl = settings.linkedInCompanyPageUrl.trim()
    }
    
    if (docSnap.exists()) {
      // Update existing document
      await setDoc(settingsRef, updateData, { merge: true })
    } else {
      // Create new document
      await setDoc(settingsRef, {
        ...updateData,
        createdAt: serverTimestamp()
      })
    }
  } catch (error) {
    console.error('Error saving LinkedIn settings:', error)
    throw error
  }
}